import React, { useEffect, useState } from "react";
import {
  Car,
  House,
  Drop,
  Lightning,
  CheckCircle,
  Sparkle,
  Warning,
} from "@phosphor-icons/react";
import { apiClient } from "../../lib/api";

const ITEMS = [
  { key: "plans_ev", icon: Car, label: "Plan to buy an EV", desc: "+~350 kWh/mo" },
  { key: "plans_pool", icon: Drop, label: "Adding a pool/hot tub", desc: "+~400 kWh/mo" },
  { key: "plans_adu", icon: House, label: "Building an ADU/Ohana", desc: "+~400 kWh/mo" },
  { key: "plans_electrification", icon: Lightning, label: "Switching gas to electric", desc: "+~250 kWh/mo" },
];

export default function StepFuturePlans({
  value,
  onChange,
  onContinue,
  hasEv = false,
  hasPool = false,
  utilityProvider = "HECO",
}) {
  const update = (k, v) => onChange({ ...value, [k]: v });
  const [costs, setCosts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.post("/future/grid-cost", {
          plans_ev: value.plans_ev,
          plans_pool: value.plans_pool,
          plans_adu: value.plans_adu,
          plans_electrification: value.plans_electrification,
          has_ev: hasEv,
          has_pool: hasPool,
          utility_provider: utilityProvider,
        });
        if (!cancelled) setCosts(data);
      } catch (e) {
        // silent
      }
    })();
    return () => { cancelled = true; };
  }, [value, hasEv, hasPool, utilityProvider]);

  const itemCost = (key) => {
    const it = costs?.items?.find((i) => i.id === key);
    return it ? `+$${Math.round(it.monthly_cost)}/mo` : null;
  };

  return (
    <div data-testid="step-future" className="max-w-3xl mx-auto">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-1.5 mb-5">
          <Sparkle size={14} weight="fill" className="text-emerald-600" />
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
            Future-proof your system
          </span>
        </div>
        <h2 className="font-display text-4xl font-bold text-slate-950 tracking-tight">
          Any of these in the next 5 years?
        </h2>
        <p className="mt-3 text-slate-600">
          We'll show what each adds to your grid bill <em>without</em> solar — so you can see what
          right-sizing solar today saves tomorrow.
        </p>
      </div>

      <div className="card-clean p-8">
        <div className="grid sm:grid-cols-2 gap-3">
          {ITEMS.map((item) => {
            const checked = !!value[item.key];
            const c = itemCost(item.key);
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => update(item.key, !checked)}
                data-testid={`future-${item.key}`}
                className={`flex items-center gap-4 text-left p-5 rounded-2xl border-2 transition-all ${
                  checked
                    ? "border-sky-500 bg-sky-50"
                    : "border-slate-200 hover:border-slate-300 bg-white"
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    checked ? "bg-sky-100" : "bg-slate-100"
                  }`}
                >
                  <item.icon
                    size={24}
                    weight="duotone"
                    className={checked ? "text-sky-600" : "text-slate-500"}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900">{item.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{item.desc}</div>
                  {checked && c && (
                    <div className="text-xs font-bold text-amber-700 font-mono mt-1.5" data-testid={`future-cost-${item.key}`}>
                      {c} on your grid bill
                    </div>
                  )}
                </div>
                {checked && <CheckCircle size={22} weight="fill" className="text-sky-500" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Total grid cost callout */}
      {costs && costs.total_monthly_cost > 0 && (
        <div
          data-testid="future-total-cost"
          className="mt-6 rounded-3xl bg-gradient-to-br from-red-500/10 to-amber-500/10 border-2 border-amber-500/30 p-6 flex items-start gap-4"
        >
          <Warning size={28} weight="fill" className="text-amber-600 flex-shrink-0 mt-1" />
          <div className="flex-1">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700 mb-1">
              Without solar offset
            </div>
            <div className="font-display text-2xl md:text-3xl font-bold text-slate-950">
              These plans add{" "}
              <span className="text-red-600">+${Math.round(costs.total_monthly_cost)}/mo</span>{" "}
              to your bill
              <span className="text-slate-700 text-lg font-medium"> · ${Math.round(costs.total_annual_cost).toLocaleString()}/year</span>
            </div>
            <p className="text-sm text-slate-700 mt-2">
              That's pure grid cost. Right-sizing solar today means these future loads run on your
              own power — for years to come.
            </p>
          </div>
        </div>
      )}

      <div className="mt-8 flex justify-center">
        <button
          data-testid="future-continue-btn"
          onClick={onContinue}
          className="btn-primary inline-flex items-center gap-2"
        >
          Calculate my savings →
        </button>
      </div>
    </div>
  );
}
